/**
 * Why a run waits for a runner, in words for agents (`read_item`, `read_run`, the turn file and the daily check).
 * Moved from the cloud's read tools (decision 0156) so core's loose ends and insights say the same thing. The web has
 * its own words for people (`apps/web/src/model/stops.ts`).
 */
import type { Unmet } from "@minga/protocol"

const hhmm = (ms: number): string => new Date(ms).toISOString().slice(11, 16)

/** One waiting reason in words ("profile codex-personal on jannik-mbp is not ready: …"). */
export const unmetText = (u: Unmet, name: (runnerId: string) => string = (id) => id): string => {
  switch (u._tag) {
    case "no_runner_online":
      return "no runner of the run's owner is online"
    case "runner_offline":
      return `waiting for runner ${name(u.runnerId)} (${u.lastSeenAt === null ? "never seen" : `offline since ${hhmm(u.lastSeenAt)} UTC`})`
    case "runner_paused":
      return `runner ${name(u.runnerId)} is paused`
    case "owner_lock":
      // decision 0128 (0126 §5): no subscription of the asker's own and none shared with the team; decision 0166 §3.2:
      // or into this project
      return u.profileId
        ? `${u.profileId} on ${name(u.runnerId ?? "")} is another person's subscription, not shared in this project: ${u.platformRunner ? "the asker connects their own on the Platform Runner, or " : ""}its owner shares it in this project (Minga asks them in their Needs you)`
        : "nothing its asker may use can run it: they connect their own subscription on the Platform Runner, or its owner shares theirs in this project"
    case "ceiling_refuses":
      return `${name(u.runnerId)} refuses it: ${u.why}`
    case "missing_tool":
      return `no runner has the tool ${u.tool}`
    case "missing_repo":
      return `no runner has a clone of repo ${u.repoId}`
    case "missing_agent":
      return `no runner has the ${u.agent.join(" or ")} CLI`
    case "profile_not_ready":
      return `profile ${u.profileId} on ${name(u.runnerId)} is not ready: ${u.why}`
    case "profile_out_of_scope":
      return `profile ${u.profileId} on ${name(u.runnerId)} is not scoped to this project`
    case "not_chosen":
      // decision 0166 §3.6
      return `${u.profileId} on ${name(u.runnerId)} is not among the subscriptions its asker chose for this project`
    case "profile_at_capacity":
      return `profile ${u.profileId} on ${name(u.runnerId)} is at capacity`
    case "rate_limited":
      return `profile ${u.profileId} on ${name(u.runnerId)} is rate limited${u.resetsAt ? ` until ${hhmm(u.resetsAt)} UTC` : ""}`
    case "at_capacity":
      return `runner ${name(u.runnerId)} is at capacity`
    case "exclusive_held":
      return `${u.key} is held by another run`
    case "affinity_unmet":
      return "waiting for the runner that has the worktree"
    case "runner_outdated":
      return `runner ${name(u.runnerId)} is too old (${u.version}; needs ${u.minimum})`
    case "missing_secret":
      // decision 0141: a person adds it, shares theirs, or lists this project on it (Settings > Secrets)
      return u.why === "not_shared"
        ? `the secret ${u.name} is ${u.ownerId ?? "a teammate"}'s personal one: they share it with the team in Settings > Secrets`
        : u.why === "not_in_project"
          ? `the team secret ${u.name} does not list this project: a workspace admin adds the project to it in Settings > Secrets`
          : `the secret ${u.name} is not added: a person adds it in Project settings > Secrets (or Settings > Secrets for the team)`
  }
}

/**
 * Decision 0156: all of a waiting run's reasons in one clause, or what an empty list means: no reason was recorded (a
 * run is between offers, or a fault Minga should be told about).
 */
export const waitText = (unmet: ReadonlyArray<Unmet>, name?: (runnerId: string) => string): string =>
  unmet.length === 0
    ? "no reason recorded (no runner was found and none said why: tell a person, this is a Minga fault)"
    : unmet.map((u) => unmetText(u, name)).join("; ")
